import { useState } from "react";
import { getCurrentToken } from "../backend/fetchApi";
import { CouponShare } from "../models/couponshares";

export type CashoutResult =
    | { success: true }
    | { success: false; error: string };

export interface UseCashoutResult {
    cashoutValue: CouponShare['cashoutValue'];
    showCashout: boolean;
    isCashingOut: boolean;
    showConfirm: boolean;
    result: CashoutResult | null;
    requestCashout: () => void;
    confirmCashout: () => Promise<void>;
    closeConfirm: () => void;
    closeResult: () => void;
}

const useCashout = (couponShare?: CouponShare): UseCashoutResult => {
    const [result, setResult] = useState<CashoutResult | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isCashingOut, setIsCashingOut] = useState(false);

    const cashoutValue = couponShare?.cashoutValue;
    // Defence in depth: the API already omits cashoutValue for syndicate shares.
    const isSyndicateShare = !!couponShare?.coupon?.coupon?.syndicateId;

    const requestCashout = () => {
        const cashoutLink = couponShare?._links?.cashout?.href;
        if (!cashoutLink) return;
        if (isCashingOut) return;
        if (isSyndicateShare) return;

        // Show confirmation dialog first
        setShowConfirm(true);
    };

    const confirmCashout = async () => {
        setShowConfirm(false);
        const cashoutLink = couponShare?._links?.cashout?.href;
        if (!cashoutLink) return;
        setIsCashingOut(true);
        try {
            const headers = new Headers({ Accept: 'application/json' });
            const token = getCurrentToken();
            if (token) headers.set('Authorization', 'Bearer ' + token);
            const res = await fetch(new URL(cashoutLink), { headers, method: 'POST' });
            if (res.ok) {
                setResult({ success: true });
                // Keep the button disabled while the cashed-out state propagates
                // and the button turns green / disappears.
            } else {
                const data = await res.json();
                setResult({ success: false, error: `${res.status} - ${JSON.stringify(data)}` });
                setIsCashingOut(false);
            }
        } catch (e: any) {
            setResult({ success: false, error: e.message });
            setIsCashingOut(false);
        }
    };

    return {
        cashoutValue,
        showCashout: !!cashoutValue && !isSyndicateShare,
        isCashingOut,
        showConfirm,
        result,
        requestCashout,
        confirmCashout,
        closeConfirm: () => setShowConfirm(false),
        closeResult: () => setResult(null),
    };
};

export default useCashout;
